import type { ComponentType } from 'react';
import {
  getCanonicalViewRegistryEntry,
} from '@shared/types/view-registry';
import type {
  ViewPatternDataMap,
  ViewPatternKey,
} from '@shared/types/views';
import { WorkDocument } from '@/components/work/WorkDocument';
import { ActionListView } from '@/components/views/ActionListView';
import { DataTableView } from '@/components/views/DataTableView';
import { EntitySummaryView } from '@/components/views/EntitySummaryView';
import { FallbackPanelView } from '@/components/views/FallbackPanelView';
import { HealthGaugeView } from '@/components/views/HealthGaugeView';
import { MetricGridView } from '@/components/views/MetricGridView';
import { ScoreCardView } from '@/components/views/ScoreCardView';
import { ChartView } from '@/components/views/chart/ChartView';
import { TimelineView } from '@/components/views/timeline/TimelineView';

type FrontendViewPatternComponentProps<TPatternKey extends ViewPatternKey> = {
  data: ViewPatternDataMap[TPatternKey];
};

export interface FrontendViewPatternRegistration<TPatternKey extends ViewPatternKey = ViewPatternKey> {
  patternKey: TPatternKey;
  // Provenance/parity metadata only. Rendering must still route by patternKey.
  envelopeViewType: ReturnType<typeof getCanonicalViewRegistryEntry<TPatternKey>>['envelopeViewType'];
  Component: ComponentType<FrontendViewPatternComponentProps<TPatternKey>>;
  isFallback?: boolean;
}

type FrontendViewPatternRegistry = {
  [K in ViewPatternKey]: FrontendViewPatternRegistration<K>;
};

export const FRONTEND_VIEW_PATTERN_REGISTRY = {
  artifact_document: {
    patternKey: 'artifact_document',
    envelopeViewType: getCanonicalViewRegistryEntry('artifact_document').envelopeViewType,
    Component: WorkDocument,
  },
  score_card: {
    patternKey: 'score_card',
    envelopeViewType: getCanonicalViewRegistryEntry('score_card').envelopeViewType,
    Component: ScoreCardView,
  },
  action_list: {
    patternKey: 'action_list',
    envelopeViewType: getCanonicalViewRegistryEntry('action_list').envelopeViewType,
    Component: ActionListView,
  },
  metric_grid: {
    patternKey: 'metric_grid',
    envelopeViewType: getCanonicalViewRegistryEntry('metric_grid').envelopeViewType,
    Component: MetricGridView,
  },
  health_gauge: {
    patternKey: 'health_gauge',
    envelopeViewType: getCanonicalViewRegistryEntry('health_gauge').envelopeViewType,
    Component: HealthGaugeView,
  },
  entity_summary: {
    patternKey: 'entity_summary',
    envelopeViewType: getCanonicalViewRegistryEntry('entity_summary').envelopeViewType,
    Component: EntitySummaryView,
  },
  data_table: {
    patternKey: 'data_table',
    envelopeViewType: getCanonicalViewRegistryEntry('data_table').envelopeViewType,
    Component: DataTableView,
  },
  chart: {
    patternKey: 'chart',
    envelopeViewType: getCanonicalViewRegistryEntry('chart').envelopeViewType,
    Component: ChartView,
  },
  timeline: {
    patternKey: 'timeline',
    envelopeViewType: getCanonicalViewRegistryEntry('timeline').envelopeViewType,
    Component: TimelineView,
  },
  fallback_empty: {
    patternKey: 'fallback_empty',
    envelopeViewType: getCanonicalViewRegistryEntry('fallback_empty').envelopeViewType,
    Component: FallbackPanelView,
    isFallback: true,
  },
} as const satisfies FrontendViewPatternRegistry;

export const getFrontendViewPatternRegistration = <TPatternKey extends ViewPatternKey>(
  patternKey: TPatternKey,
): FrontendViewPatternRegistration<TPatternKey> => (
  FRONTEND_VIEW_PATTERN_REGISTRY[patternKey] as unknown as FrontendViewPatternRegistration<TPatternKey>
);
